import React from 'react';

import { DefaultContext } from '@apollo/client';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Grid, InputAdornment, Typography } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { convertDollarsToCents } from '@clearverify/utils';

import { Button } from 'src/components/common';
import { Field, Form, NumberField } from 'src/components/finalForm';
import { verifierBillingValidationSchema } from 'src/constants/validation/billing';
import { useNotification } from 'src/hooks/app';
import { useVerifierParamId } from 'src/hooks/verifier';
import { useVerifierStaffList } from 'src/hooks/verifierStaff';
import { getVerifierBillingRulesFormInitialValues } from 'src/pages/verifiers/utils/verifierBillingRulesForm';
import { VerifierBillingSettingsMutationDataType } from 'src/types/billing';

import { verifierBillingRulesInputFieldsVariables } from './constants';
import { VerifierBillingRulesFormValues } from './types';

interface BillingRulesFormProps extends VerifierBillingRulesFormValues {
  updateVerifierBillingRules: (params: {
    data: VerifierBillingSettingsMutationDataType;
    context: DefaultContext;
  }) => Promise<void>;
}

export const BillingRulesForm: React.FC<BillingRulesFormProps> = ({
  voI,
  voE,
  reverification,
  updateVerifierBillingRules,
}) => {
  const { createApolloContext } = useNotification();
  const verifierId = useVerifierParamId();
  const { verifierStaffList, loading } = useVerifierStaffList(verifierId);

  const initialValues: VerifierBillingRulesFormValues = React.useMemo(
    () =>
      getVerifierBillingRulesFormInitialValues({
        voI,
        voE,
        reverification,
      }),
    [voI, voE, reverification],
  );

  const paymentMethodLabel = () => {
    const isPaymentMethodConfigured = verifierStaffList.some(
      verifierStaff => verifierStaff.stripeCustomerId,
    );

    if (isPaymentMethodConfigured) {
      return (
        <Grid container spacing={1}>
          <Grid>Payment method is configured</Grid>
          <Grid>
            <CheckCircleIcon style={{ color: '#388E3C' }} color="success" />
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container spacing={1}>
        <Grid>Payment method is not configured</Grid>
        <Grid>
          <ErrorIcon style={{ color: '#FF3D00' }} color="error" />
        </Grid>
      </Grid>
    );
  };

  const paymentMethodSubtitle = () => {
    const isPaymentMethodConfigured = verifierStaffList.some(
      verifierStaff => verifierStaff.stripeCustomerId,
    );

    if (isPaymentMethodConfigured) {
      return (
        <Typography fontSize="16px" color="text.secondary" variant="body1">
          Payment method has been configured successfully
        </Typography>
      );
    }
    return (
      <Typography fontSize="16px" color="text.secondary" variant="body1">
        You need to set up a Payment method to create Verification Requests on the platform
      </Typography>
    );
  };

  const handleSubmit = React.useCallback(
    async (formValues: VerifierBillingRulesFormValues) => {
      try {
        const dataToSubmit: VerifierBillingSettingsMutationDataType = {
          verifierBilling: {
            update: {
              voI: convertDollarsToCents(formValues.voI),
              voE: convertDollarsToCents(formValues.voE),
              reverification: convertDollarsToCents(formValues.reverification),
            },
          },
        };

        await updateVerifierBillingRules({
          data: dataToSubmit,
          context: createApolloContext({
            successMessage: 'Verifier billing rules have been successfully updated',
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, updateVerifierBillingRules],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithYupSchema(verifierBillingValidationSchema)}
    >
      {({ dirty, submitting, valid, form: { reset } }) => (
        <React.Fragment>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Grid container p={2} pl={4} border={1} borderRadius={2} borderColor="border.light">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Grid size={12}>
                    <Typography
                      sx={{ display: 'flex' }}
                      component="h3"
                      variant="subtitle1"
                      fontSize="16px"
                    >
                      {paymentMethodLabel()}
                    </Typography>
                    <Typography component="p" variant="h3" fontSize="16px" lineHeight="24px">
                      {paymentMethodSubtitle()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {verifierBillingRulesInputFieldsVariables.map(inputValue => (
              <Grid key={inputValue.name} size={12}>
                <Grid container p={2} pl={4} border={1} borderRadius={2} borderColor="border.light">
                  <Grid size={12}>
                    <Typography component="h3" variant="subtitle1" fontSize="16px">
                      {inputValue.title}
                    </Typography>
                    <Typography
                      mb={2}
                      color="text.secondary"
                      component="p"
                      variant="h3"
                      fontSize="16px"
                      lineHeight="24px"
                    >
                      {inputValue.description}
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid mt={2} size={12}>
                    <Field
                      fullWidth
                      label={inputValue.label}
                      name={inputValue.name}
                      component={NumberField}
                      disabled={submitting}
                      placeholder="0"
                      inputProps={{
                        endAdornment: <InputAdornment position="end">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Box display="flex" gap={2} mt={3} justifyContent="center">
            <Button variant="outlined" color="error" onClick={() => reset()} disabled={submitting}>
              Reset
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !dirty || !valid}
              loading={submitting}
            >
              Save Changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
